/**
 * Modal dialog component for creating a new assignment, allowing selection of tool,
 * simulation, and assignment target (user or group) with Redux-backed data fetching.
 *
 * @module components/dashboard/AddAssignment
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import {makeStyles} from '@material-ui/core/styles';
import {Button, Grid, InputLabel, Select, TextField, Tooltip,} from '@material-ui/core';
import {connect} from 'react-redux';
import {getSims} from '../../services/result.service';
import {
    addAssignment,
    fetchGroupList,
    fetchProductList,
    fetchUserList,
} from '../../redux/sims/actions';
import AddAssignmentStyle from './AddAssignment.js';

/**
 * Modal dialog for creating a new assignment by selecting a tool, simulation,
 * and assigning it to an individual user or group.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {boolean} props.open - Specifies whether the modal is open or not.
 * @param {Function} props.handleClose - Function to handle the closing of the modal.
 * @param {Function} props.addAssignment - Function to handle the submission of new assignment data.
 * @param {Function} props.fetchProductList - Function to fetch the list of tools/products.
 * @param {Array} props.products - The list of tools/products available for assignment.
 * @param {Function} props.fetchUserList - Function to fetch the list of users.
 * @param {Array} props.users - The list of users available for assignment.
 * @param {Function} props.fetchGroupList - Function to fetch the list of groups.
 * @param {Array} props.groups - The list of groups available for assignment.
 * @returns {JSX.Element} The rendered component for the Add Assignment modal.
 */
const AddAssignment = ({
    open,
    handleClose,
    addAssignment,
    fetchProductList,
    products,
    fetchUserList,
    users,
    fetchGroupList,
    groups,
}) => {
    const useStyles = makeStyles((theme) => AddAssignmentStyle(theme));
    const classes = useStyles();
    const [isGroup, setIsGroup] = useState(false);
    const [state, setState] = useState({
        tool: '',
        assigned_sim: '',
        user_id: '',
        group_id: ' ',
        // limit: '',
        assignment_notes: ' ',
    });
    const [sims, setSims] = useState([]);
    const [assignClick, setAssignClick] = useState(false);

    useEffect(() => {
        fetchProductList();
    }, [fetchProductList]);

    useEffect(() => {
        if (products.length > 0) {
            getSimsOfProduct(products[0]._id);
        }
    }, [products]);

    useEffect(() => {
        fetchUserList();
    }, [fetchUserList]);

    useEffect(() => {
        setState({
            ...state,
            group_id: groups.length > 0 ? groups[0]._id : '',
        });
    }, [groups]);

    /**
     * Fetches and processes similar products for a given product ID.
     *
     * This function retrieves a list of similar products using the provided product ID,
     * sorts the products alphabetically by their titles, and updates the state to include
     * the sorted list of similar products and the ID of the first product in the list
     * (if any are available).
     *
     * @param {string} productId - The unique identifier of the product for which to fetch similar
     *     products.
     */
    const getSimsOfProduct = (productId) => {
        getSims(productId)
            .then((res) => {
                setSims(res.data.sort((a, b) => a.title.localeCompare(b.title)));
                setState((prevData) => ({
                    ...prevData,
                    assigned_sim: res.data.length > 0 ? res.data[0]._id : '',
                }));
            });
    };

    /**
     * Handles change events for input fields and updates the component's state accordingly.
     * The updated state includes the current field's name and value pair.
     * Additionally, performs a specific action if the name of the changed field matches 'tool'.
     *
     * @param {Object} event - The event object triggered by the input field change.
     * @param {string} event.target.name - The name attribute of the target element.
     * @param {string} event.target.value - The value of the input field.
     */
    const handleChange = (event) => {
        setState({
            ...state,
            [event.target.name]: event.target.value,
        });
        if (event.target.name === 'tool') {
            getSimsOfProduct(event.target.value);
        }
    };


    /**
     * Executes the next step in the process based on the current state.
     *
     * This function handles a conditional action based on whether `isGroup` is true or false.
     * If `isGroup` is true, it resets the `user_id` property of the state and updates the state.
     * Otherwise, it resets the `group_id` property of the state and updates the state.
     * It also updates the `assignClick` state to true, performs an asynchronous assignment
     * operation, and closes the handler by invoking the `handleClose` function.
     *
     * @async
     * @function handleNext
     */
    const handleNext = async () => {
        if (isGroup) {
            state.user_id = '';
            setState(state);
        } else {
            state.group_id = '';
            setState(state);
        }
        setAssignClick(true);
        await addAssignment(state);
        handleClose(false);
    };


    /**
     * Toggles the state of the `isGroup` variable and performs an associated action.
     *
     * When `isGroup` is `false`, it sets `isGroup` to `true` and initiates the loading
     * of a group list by calling the `fetchGroupList` function. When `isGroup` is `true`,
     * it sets `isGroup` to `false`.
     *
     * This function is typically used to toggle between group-related and non-group-related states.
     */
    const groupChange = () => {
        if (isGroup === false) {
            setIsGroup(true);
            fetchGroupList();
        } else {
            setIsGroup(false);
        }
    };

    if (
        (products.length > 0 && state.tool === '') ||
        (sims.length > 0 && state.assigned_sim === '') ||
        (users.length > 0 && state.user_id === '')
    ) {
        setState({
            ...state,
            tool: products.length > 0 ? products[0]._id : '',
            assigned_sim: sims.length > 0 ? sims[0]._id : '',
            user_id: users.length > 0 ? users[0]._id : '',
            group_id: groups.length > 0 ? groups[0]._id : '',
        });
    }

    return (
        <div>
            <Dialog
                onClose={() => handleClose(false)}
                aria-labelledby="details-screen"
                open={open}
                fullWidth
                className={classes.root}
            >
                <div className={classes.modalBody}>
                    <DialogTitle id="details-screen" className={classes.title}>
                        <br/>
                        Add New Assignment
                    </DialogTitle>
                    <InputLabel className={classes.labelFont}>
                        (Fill the following details in the order shown below)
                    </InputLabel>
                    <Grid container className={classes.container}>
                        <Grid item xs={12} lg={12}>
                            <InputLabel className={classes.label}>1. Select Tool</InputLabel>
                            <Select
                                variant="outlined"
                                name="tool"
                                size="small"
                                value={state.tool}
                                onChange={handleChange}
                                className={classes.select}
                                native
                            >
                                {products && products.map(
                                    (product) => (
                                        <option key={product._id} value={product._id}>
                                            {product.name}
                                        </option>
                                    ),
                                )}
                            </Select>
                        </Grid>
                        <Grid item xs={12} lg={12}>
                            <InputLabel className={classes.label}>2. Select Assignment</InputLabel>
                            <Select
                                variant="outlined"
                                name="assigned_sim"
                                size="small"
                                value={state.assigned_sim}
                                onChange={handleChange}
                                className={classes.select}
                                native
                            >
                                {sims && sims.map(
                                    (sim) => (
                                        <Tooltip key={sim._id} title={sim.title.length === 0 ?
                                            'None' :
                                            sim.title}>
                                            <option key={sim._id} value={sim._id}>
                                                {sim.title.length > 50 ?
                                                    `${sim.title.slice(0, 50)}  ...`
                                                    :
                                                    sim.title}
                                            </option>
                                        </Tooltip>
                                    ),
                                )}
                            </Select>
                        </Grid>
                        {
                            isGroup ? (

                                <Grid item xs={12} lg={12}>
                                    <InputLabel className={classes.label}>
                                        3. Select a Group
                                        <span onClick={groupChange} className={classes.link}>
                                            (Or assign to a member)
                                        </span>
                                    </InputLabel>
                                    <Select
                                        variant="outlined"
                                        name="group_id"
                                        size="small"
                                        value={state.member}
                                        onChange={handleChange}
                                        className={classes.select}
                                        native
                                    >
                                        {groups && groups.map(
                                            (group) => (
                                                <option key={group._id} value={group._id}>
                                                    {group.name}
                                                </option>
                                            ),
                                        )}
                                    </Select>
                                </Grid>

                            ) : (
                                <Grid item xs={12} lg={12}>
                                    <InputLabel className={classes.label}>
                                        3. Select member
                                        <span onClick={groupChange} className={classes.link}>
                                            (Or assign to a group)
                                        </span>
                                    </InputLabel>
                                    <Select
                                        variant="outlined"
                                        name="user_id"
                                        size="small"
                                        value={state.member}
                                        onChange={handleChange}
                                        className={classes.select}
                                        native
                                    >
                                        {users && users.map(
                                            (user) => (
                                                <option key={user._id} value={user._id}>
                                                    {`${user.first_name} ${user.last_name}`}
                                                </option>
                                            ),
                                        )}
                                    </Select>
                                </Grid>

                            )
                        }

                        {/* <Grid item xs={12} lg={12}>
                         <InputLabel className={classes.label}>
                         4.Include Limit?
                         <span className={classes.link}> (Limits explained)</span>
                         </InputLabel>
                         <TextField
                         variant="filled"
                         name="limit"
                         label="Limit"
                         size="small"
                         type="number"
                         className={classes.text}
                         onChange={handleChange}
                         />
                         </Grid> */}
                        <Grid item xs={12} lg={12}>
                            <InputLabel className={classes.label}>
                                4. Assignment Instruction
                            </InputLabel>
                            <TextField
                                fullWidth
                                name="assignment_notes"
                                multiline
                                minRows={4}
                                InputProps={{
                                    disableUnderline: true,
                                }}
                                className={classes.notes}
                                placeholder="Type notes here"
                                variant="filled"
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={6} lg={6}>
                            <Button
                                variant="outlined"
                                className={classes.cancel}
                                disableElevation
                                color="primary"
                                onClick={() => handleClose(false)}
                            >
                                CANCEL
                            </Button>
                        </Grid>
                        <Grid item xs={6} lg={6}>
                            <Button
                                variant="contained"
                                color="primary"
                                className={classes.assignButton}
                                disableElevation
                                onClick={handleNext}
                                disabled={!(state.tool && state.assigned_sim
                                    && state.user_id) || assignClick}
                                // disabled={!(state.tool && state.assigned_sim
                                //     && state.user_id && state.limit)}
                            >
                                ASSIGN
                            </Button>
                        </Grid>
                    </Grid>
                </div>
            </Dialog>
        </div>
    );
};

/**
 * PropTypes definition for the AddAssignment component.
 *
 * This specifies the expected types and requirements for the props
 * passed to the AddAssignment component, ensuring proper use
 * and validation of input data.
 */
AddAssignment.propTypes = {
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    addAssignment: PropTypes.func,
    fetchProductList: PropTypes.func,
    fetchUserList: PropTypes.func,
    fetchGroupList: PropTypes.func,
    products: PropTypes.array,
    users: PropTypes.array,
    groups: PropTypes.array,
};

/**
 * Maps the Redux state to the component's props.
 *
 * @param {Object} state - The current Redux state.
 * @returns {Object} An object containing specific pieces of state to be passed as props:
 * - `newAssignment`: The new assignment data from the state.
 * - `products`: The list of products, or an empty array if not available.
 * - `users`: The list of users, or an empty array if not available.
 * - `groups`: The list of groups, or an empty array if not available.
 */
const mapStateToProps = (state) => ({
    newAssignment: state.sim.newAssignment,
    products: state.sim.products ? state.sim.products : [],
    users: state.sim.users ? state.sim.users : [],
    groups: state.sim.groups ? state.sim.groups : [],
});

/**
 * Maps Redux action creators to component props for dispatching assignment-related actions.
 *
 * @type {Object}
 */
const mapDispatchToProps = {
    addAssignment, fetchProductList, fetchUserList, fetchGroupList,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddAssignment);
